/**
 * usePublishRecordDownload.ts
 *
 * Shared download handler for a succeeded PublishRecord — resolves a signed
 * URL for the rendered variant and triggers a browser download. Used by both
 * the Downloads tab and the Export workflow's completed state.
 */

import { resolvePublishRecordVideoUrl } from '@src/services/publish/publishRecordVideoUrl';
import { downloadVideo } from '@src/services/videoDownloadService';
import { extractErrorInfo } from '@src/utility/error';
import { sanitizeFileName } from '@src/utility/filename';
import { LogManager } from '@src/utility/Logger';
import { useCallback, useState } from 'react';

import type { PublishRecord } from '@src/types.ts/publishRecord';

const logger = LogManager.getLogger('hook:usePublishRecordDownload');

/**
 * Build the sanitized, per-variant download file name for a PublishRecord:
 * `{base}_{lang}_{ar}_{res}`. The AR's ':' would be stripped by
 * `sanitizeFileName` (9:16 -> 916), so render it as the filename-safe dimension
 * form (9x16) first. Sanitized here because `downloadVideo` uses it verbatim.
 */
function buildPublishRecordFileName(
  record: PublishRecord,
  fileNameBase?: string,
): string {
  const base = fileNameBase || record.videoId || 'video';
  const aspectRatioLabel = record.aspectRatio.replaceAll(':', 'x');
  return sanitizeFileName(
    `${base}_${record.language}_${aspectRatioLabel}_${record.resolution}`,
  );
}

interface UsePublishRecordDownloadReturn {
  /** Id of the record currently downloading, or null. */
  downloadingId: string | null;
  /**
   * Fetch a signed URL for the record's rendered video and download it.
   * `fileNameBase` names the file `{base}_{lang}_{ar}_{res}` — pass the clip
   * title when the caller is scoped to one clip (Downloads tab); omit it in
   * global surfaces (the progress drawer), where it falls back to the record's
   * videoId. It's a per-download arg, not a hook option, because one hook
   * instance can download records belonging to different clips.
   */
  download: (record: PublishRecord, fileNameBase?: string) => Promise<void>;
}

export function usePublishRecordDownload(
  /** Analytics attribution for the `clip_downloaded` event. */
  sourcePlatform: string = 'content-studio',
): UsePublishRecordDownloadReturn {
  const [downloadingId, setDownloadingId] = useState<string | null>(null);

  const download = useCallback(
    async (record: PublishRecord, fileNameBase?: string) => {
      if (!record.outputJson?.video?.fileKey) return;
      setDownloadingId(record.id);
      try {
        const url = await resolvePublishRecordVideoUrl(record);

        if (url) {
          // Route through the shared download service so the `clip_downloaded`
          // analytics event + error handling stay in one place.
          const result = await downloadVideo({
            url,
            sanitizedFileName: buildPublishRecordFileName(record, fileNameBase),
            metadata: {
              clipId: record.videoId,
              storyId: record.storyId,
              aspectRatio: record.aspectRatio,
              sourcePlatform,
            },
          });
          if (!result.ok) {
            logger.error('Download failed', {
              recordId: record.id,
              error: result.error,
            });
          }
        }
      } catch (err) {
        logger.error('Download failed', {
          recordId: record.id,
          error: extractErrorInfo(err),
        });
      } finally {
        setDownloadingId(null);
      }
    },
    [sourcePlatform],
  );

  return { downloadingId, download };
}
